import * as yup from "yup";
import { useNavigate, Navigate } from "react-router-dom";
import { useFormik } from "formik";
import React, { useState, useEffect, useRef } from "react";

import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import { Box, TextareaAutosize } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import Title from "../../../common/Title";
import InputFieldBox from "../../../common/InputFieldBox";

import useAlert from "../../../../hooks/useAlert";
import useAuth from "../../../../hooks/useAuth";
import useAxios from "../../../../hooks/useAxios";
import { __clientActivatePartner } from "../../../../api/sim";
import { isPartnerIccidDigits } from "../../../../utils/value";

const parseRequestedLines = (value) => {
  if (value === "" || value === null || value === undefined) {
    return NaN;
  }
  const n = typeof value === "number" ? value : parseInt(String(value), 10);
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Client virtual activation: same location rules as admin T-Mobile J form; plan from user profile.
 */
const ClientPartnerActivateForm = () => {
  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rawData, setRawData] = useState("");
  const [locationData, setLocationData] = useState([]);
  const [locationType, setLocationType] = useState(null);
  const responseRef = useRef(null);

  const { setAlertMessage } = useAlert();
  const { user } = useAuth();
  const [response, call, loading] = useAxios({
    ...__clientActivatePartner,
  });
  const nav = useNavigate();

  const initialData = {
    requested_lines: 0,
    skip_zip_checks: false,
  };

  const validateFunction = (data, requestedLines) => {
    const errors = {};
    const formattedLines = [];
    let detectedType = null;

    if (!data || !data.trim()) {
      setLocationData([]);
      setLocationType(null);
      return errors;
    }

    const lines = data
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length === 0) {
      setLocationData([]);
      setLocationType(null);
      return errors;
    }

    const reqNum = parseRequestedLines(requestedLines);
    if (!Number.isFinite(reqNum) || reqNum < lines.length) {
      errors.requested_lines = `Requested lines must be at least ${lines.length} to match the number of location entries (area code, zip, or ICCID line).`;
      setLocationData([]);
      setLocationType(null);
      return errors;
    }

    lines.some((line, idx) => {
      const trimmedLine = line.trim();

      if (trimmedLine.includes(",")) {
        const parts = trimmedLine
          .split(",")
          .map((part) => part.trim())
          .filter((part) => part.length > 0);

        if (parts.length > 2) {
          errors.locations = `Invalid format in line ${idx + 1}: ${trimmedLine}. Format should be: ICCID,areacode or ICCID,zipcode or just areacode/zipcode.`;
          return true;
        }

        const firstPart = parts[0];
        const secondPart = parts[1];

        if (!/^\d{19,20}$/.test(firstPart)) {
          errors.locations = `Invalid ICCID in line ${idx + 1}: ${firstPart}. ICCID must be 19 or 20 digits.`;
          return true;
        }

        if (!isPartnerIccidDigits(firstPart)) {
          errors.locations = "Invalid ICCID provided";
          return true;
        }

        if (secondPart) {
          if (/^\d{3}$/.test(secondPart)) {
            if (detectedType === null) {
              detectedType = "areacode";
            } else if (detectedType !== "areacode") {
              errors.locations = `Cannot mix area codes and zip codes. Line ${idx + 1} contains an area code but previous lines contain zip codes.`;
              return true;
            }
            formattedLines.push({ iccid: firstPart, areacode: secondPart.trim() });
          } else if (/^\d{5}$/.test(secondPart)) {
            if (detectedType === null) {
              detectedType = "zipcode";
            } else if (detectedType !== "zipcode") {
              errors.locations = `Cannot mix area codes and zip codes. Line ${idx + 1} contains a zip code but previous lines contain area codes.`;
              return true;
            }
            formattedLines.push({ iccid: firstPart, zipcode: secondPart.trim() });
          } else {
            errors.locations = `Invalid location format in line ${idx + 1}: ${secondPart}. Area codes must be 3 digits and zip codes must be 5 digits.`;
            return true;
          }
        } else {
          formattedLines.push({ iccid: firstPart });
        }
        return false;
      }

      if (/^\d{3}$/.test(trimmedLine)) {
        if (detectedType === null) {
          detectedType = "areacode";
        } else if (detectedType !== "areacode") {
          errors.locations = `Cannot mix area codes and zip codes. Line ${idx + 1} contains an area code but previous lines contain zip codes.`;
          return true;
        }
        formattedLines.push({ areacode: trimmedLine.trim() });
        return false;
      }

      if (/^\d{5}$/.test(trimmedLine)) {
        if (detectedType === null) {
          detectedType = "zipcode";
        } else if (detectedType !== "zipcode") {
          errors.locations = `Cannot mix area codes and zip codes. Line ${idx + 1} contains a zip code but previous lines contain area codes.`;
          return true;
        }
        formattedLines.push({ zipcode: trimmedLine.trim() });
        return false;
      }

      if (/^\d{19,20}$/.test(trimmedLine)) {
        if (!isPartnerIccidDigits(trimmedLine)) {
          errors.locations = "Invalid ICCID provided";
          return true;
        }
        formattedLines.push({ iccid: trimmedLine });
        return false;
      }

      errors.locations = `Invalid format in line ${idx + 1}: ${trimmedLine}. Format should be: ICCID (19-20 digits), area code (3 digits), zip code (5 digits), or ICCID,areacode/zipcode.`;
      return true;
    });

    if (!errors.locations) {
      setLocationData(formattedLines);
      setLocationType(detectedType);
    }

    return errors;
  };

  const validationSchema = yup.object({
    requested_lines: yup
      .number("requested_lines")
      .required("Requested lines is required")
      .min(1, "Requested lines must be at least 1")
      .integer("Requested lines must be a whole number"),
    skip_zip_checks: yup.boolean("skip_zip_checks").required(),
    locations: yup.string().optional(),
  });

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      const vf = validateFunction(
        rawData,
        parseRequestedLines(values.requested_lines)
      );
      if (vf.requested_lines || vf.locations) {
        applyPartnerValidationErrors(vf);
        return;
      }

      const lines = locationData.length > 0 ? [...locationData] : [];

      const requestData = {
        requested_lines: Number(values.requested_lines),
        skip_zip_checks: values.skip_zip_checks,
        ...(lines.length > 0 && { lines: lines }),
      };

      setFormData(requestData);
    },
    validationSchema: validationSchema,
    validateOnChange: true,
  });

  const applyPartnerValidationErrors = (errors) => {
    const next = { ...formik.errors };
    if (errors.locations) {
      next.locations = errors.locations;
    } else {
      delete next.locations;
    }
    if (errors.requested_lines) {
      next.requested_lines = errors.requested_lines;
    } else {
      delete next.requested_lines;
    }
    formik.setErrors(next);
  };

  useEffect(() => {
    if (formData && !isSubmitting) {
      setIsSubmitting(true);
      formik.setSubmitting(true);
      const apiPayload = {
        requested_lines: formData.requested_lines,
        skip_zip_checks: formData.skip_zip_checks,
      };
      if (formData.lines && formData.lines.length > 0) {
        apiPayload.lines = formData.lines;
      }
      call({
        data: apiPayload,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, call, isSubmitting]);

  useEffect(() => {
    if (!response || !isSubmitting) return;

    if (
      responseRef.current &&
      responseRef.current.status === response.status &&
      JSON.stringify(responseRef.current.data) === JSON.stringify(response.data)
    ) {
      return;
    }

    if (response.status) {
      setAlertMessage({ type: "success", message: "Activation queue started" });
      nav("/client/activate");
    }

    responseRef.current = response;
    formik.setSubmitting(false);
    setIsSubmitting(false);
    setFormData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, nav, setAlertMessage, isSubmitting]);

  if (user?.admin) {
    return <Navigate to="/client/activate" replace />;
  }

  if (user && !user.admin && user.virtual_plan_set === false) {
    return <Navigate to="/client/activate" replace />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Virtual activation"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.requested_lines) ? true : false}
              sx={{ width: "300px" }}
            >
              <InputFieldBox
                value={formik.values.requested_lines}
                onChange={(event) => {
                  const value = event.target.value;
                  formik.setFieldValue("requested_lines", value);
                  if (rawData) {
                    const errors = validateFunction(
                      rawData,
                      parseRequestedLines(value)
                    );
                    applyPartnerValidationErrors(errors);
                  }
                }}
                label="Requested lines"
                type="number"
                placeholder="Enter number of lines to activate"
              />

              {Boolean(formik.errors.requested_lines) ? (
                <FormHelperText id="requested_lines-error-text">
                  {formik.errors.requested_lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="locations">
              {"Add ICCID, area codes, or zip codes (optional, one per line). Plan is set on your user account."}
              <br />
              {locationType === "areacode" ? (
                <>
                  {"8901240390123456789,123 (ICCID with area code) or 123 (area only)"}
                  <br />
                  <em>
                    {
                      "ICCID must be 19–20 digits and start with 890124039 when provided."
                    }
                  </em>
                </>
              ) : locationType === "zipcode" ? (
                <>
                  {"8901240390123456789,12345 (ICCID with zip) or 12345 (zip only)"}
                  <br />
                  <em>
                    {
                      "ICCID must be 19–20 digits and start with 890124039 when provided."
                    }
                  </em>
                </>
              ) : (
                <>
                  {"890124039… (ICCID with optional area or zip), or area/zip only lines"}
                  <br />
                  <em>
                    {
                      "Use either all area codes (3 digits) OR all zip codes (5 digits), not both. ICCIDs must start with 890124039."
                    }
                  </em>
                </>
              )}
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors.locations) ? true : false}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={locationType === "areacode"
                  ? "Optional virtual ICCIDs / area codes\n8901240390123456789,123"
                  : locationType === "zipcode"
                  ? "Optional virtual ICCIDs / zip codes\n8901240390123456789,12345"
                  : "Optional — ICCID,areacode or ICCID,zipcode or area/zip/ICCID only"}
                style={{ minWidth: 550 }}
                name="locations"
                minRows={5}
                id="locations"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  const errors = validateFunction(
                    data,
                    parseRequestedLines(formik.values.requested_lines)
                  );
                  applyPartnerValidationErrors(errors);
                }}
              />
              {Boolean(formik.errors.locations) ? (
                <FormHelperText id="locations-error-text">
                  {formik.errors.locations}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.skip_zip_checks) ? true : false}
              sx={{ width: "300px" }}
            >
              <InputFieldBox
                isCheckbox={true}
                value={formik.values.skip_zip_checks}
                onChange={(event) =>
                  formik.setFieldValue("skip_zip_checks", event.target.checked)
                }
                label="Skip Zip checks"
              />

              {Boolean(formik.errors.skip_zip_checks) ? (
                <FormHelperText id="skip_zip_checks-error-text">
                  {formik.errors.skip_zip_checks}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  formik.errors.requested_lines ||
                  !formik.values.requested_lines ||
                  formik.errors.locations ||
                  formik.isSubmitting
                }
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Queue virtual activation"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ClientPartnerActivateForm;
